import { GridRow } from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import React, { useCallback, useMemo } from "react";
import { InvoiceCommentRow } from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-row.type";
import { AppGridColumn, AppGridContext, AppGridField, AppGridMeta } from "@/features/order-sheet-sets/app-grid";
import { AgencyLinkedOrderSheetRow } from "@/features/invoices/agency-invoice-row.type";
import { toLower, toTitle, toUpper } from "@/utils/case";
import AppRemittanceStatusByRowLabel from "@/features/ui/app-remittance-status-by-row-label";
import { Box, Flex } from "@chakra-ui/react";
import { ProformaInvoiceDetail } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import { BuyerLinkedOrderSheetSetRow } from "@/features/invoices/buyer-invoice-row.type";
import useAppGrid from "@/features/invoices/hooks/use-app-grid";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useLineSheetSetGrid from "@/features/order-sheet-sets/hooks/use-line-sheet-set-grid";
import {
  AbstractInvoice,
  OrderConfirmationInvoiceDetail
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppLegacyInvoiceStatusLabel from "@/features/ui/app-legacy-invoice-status-label";
import AppGridCell from "@/features/order-sheet-sets/app-grid-cell";
import AppRemittanceStatusLabel from "@/features/ui/app-remittance-status-label";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { Price, StoredObject } from "@/features/types";
import AppInvoiceStatusLabel from "@/features/ui/app-invoice-status-label";
import { MathUtils } from "@/features/ui/utils/math-utils";
import { withCrossTradeFee } from "@/features/invoices/proforma-invoices/cross-trade-fee";
import AppLabel from "@/features/ui/app-label";

interface UseInvoiceGridProps {
  rows: GridRow[] | undefined;
  mutateRows: () => void;
}

export default function useInvoiceGrid({
  rows,
  mutateRows,
}: UseInvoiceGridProps) {
  const appGrid = useAppGrid();
  const orderSheetGrid = useLineSheetSetGrid({ rows, mutateRows });
  const { navigate } = useRouterHelper();

  const { t, tCurrencyString, tLocalDateString, tNumberString } =
    useI18nHelper();
  const identity = useIdentity();

  const isIdentityAgency = useMemo(() => {
    return identity?.company?.type === "AGENCY";
  }, [identity?.company?.type]);

  const statuses = useMemo(() => {
    return {
      name: "status",
      value: (_row: GridRow, context: AppGridContext) => {
        const statusRow = _row as GridRow & {
          status: AbstractInvoice["status"];
        };
        const remittanceStatusRow = _row as GridRow & {
          isRemitted: boolean;
          isRead?: boolean;
          paymentMethod: AbstractInvoice["paymentMethod"];
        };
        return (
          <Flex flexDirection={"column"} gap={"2px"}>
            <AppRemittanceStatusByRowLabel
              row={remittanceStatusRow}
              context={context}
            />
            <AppLegacyInvoiceStatusLabel row={statusRow} context={context} />
          </Flex>
        );
      },
    };
  }, []);

  const paidByAgency = useMemo(() => {
    return {
      name: "Paid By Agency",
      value: (_row: GridRow) => {
        const row = _row as GridRow & { paidBy: string };
        const key = `paid_by_agency.${
          row.paidBy === "AGENCY" ? "agency_paid" : "buyer_paid"
        }`;
        return t(key);
      },
    };
  }, [t]);

  const paymentMethod = useMemo(() => {
    return {
      name: "Payment Method",
      value: (_row: GridRow) => {
        const row = _row as GridRow & { paymentMethod: string };
        const key = `payment_method.${toLower(row.paymentMethod)}`;
        return t(key);
      },
    };
  }, [t]);

  const exposedToBuyer = useMemo(() => {
    return {
      name: "Exposed To Buyer",
      value: (_row: GridRow) => {
        const row = _row as GridRow & { isPublic: boolean };
        return t(
          `exposed_to_buyer.${row.isPublic ? "exposed" : "not_exposed"}`
        );
      },
    };
  }, [t]);

  const creditInvoiceRowType = useMemo(() => {
    return {
      name: "type",
      value: (_row: GridRow) => {
        const row = _row as GridRow & { type: string };
        return toTitle(
          t(
            row.type === "FIXING"
              ? "fixing_credit_invoice"
              : "season_credit_invoice"
          )
        );
      },
    };
  }, [t]);

  const fields = useMemo(() => {
    return {
      statuses,
      paidByAgency,
      paymentMethod,
      exposedToBuyer,
      creditInvoiceRowType,
    };
  }, [
    statuses,
    paidByAgency,
    paymentMethod,
    exposedToBuyer,
    creditInvoiceRowType,
  ]);

  type InvoiceGridKeys =
    | "totalAmount"
    | "title"
    | "deposit"
    | "totalRequiredAmount"
    | "paymentRequiredAmount"
    | "buyerRemittedAmount"
    | "agencyRemittedAmount"
    // | "totalUnremittedAmount"
    | "remittanceStatus"
    | "paidBy"
    | "issuedBy"
    | "issuedThrough"
    | "orderedBy"
    | "tatr"
    | "issuedOn"
    | "amount"
    | "creditInvoiceType"
    | "number"
    | "season"
    | "brand"
    | "paymentMethod"
    | "exposedToBuyer"
    | "receiver"
    | "sender"
    | "remittedAmount"
    | "unremittedAmount"
    | "status"
    | "requestedOn"
    | "ownedBy"
    | "creditReason"
    | "creditUsage";

  //pairs
  const pairs = useMemo((): {
    [key in InvoiceGridKeys]: {
      column: AppGridColumn;
      field: AppGridField;
    };
  } => {
    return {
      exposedToBuyer: {
        column: {
          name: toTitle(t("exposed_to_buyer")),
          width: "104px",
        },
        field: {
          name: "exposed_to_buyer",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { isPublic: boolean };
            return t(
              `exposed_to_buyer.${row.isPublic ? "exposed" : "not_exposed"}`
            );
          },
        },
      },
      status: {
        column: {
          name: toTitle(t("status")),
          width: "72px",
        },
        field: {
          name: "status",
          value: (_row: GridRow, context: AppGridContext) => {
            const row = _row as GridRow & {
              status: AbstractInvoice["status"];
            };

            return <AppInvoiceStatusLabel value={row.status} />;
          },
        },
      },
      title: {
        column: {
          name: toTitle(t("title")),
          width: "minmax(120px, 1fr)",
        },
        field: {
          name: "title",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              name: string;
              number?: string;
              status: string;
              issuedByCompany?: { name: string; type?: string } | null;
            };
            // 3자무역 인보이스면 제목 앞에 3자무역사 이름을 붙인다
            const crossTraderPrefix =
              row.issuedByCompany?.type === "CROSS_TRADER" ? `[${row.issuedByCompany.name}] ` : "";
            return (
              <Flex alignItems={"center"} gap={"4px"}>
                {row.status === "REPLACED" && (
                  <AppLabel nameKey={"invoice_status.replaced"} level={"GRAY"} size={"small"} isToUpper={false} minW={"fit-content"} />
                )}
                {row.status === "CANCELED" && (
                  <AppLabel nameKey={"invoice_status.canceled"} level={"ERROR"} size={"small"} isToUpper={false} minW={"fit-content"} />
                )}
                {`${crossTraderPrefix}${row.number ? `[${row.number}]` : ""}${row.name}`}
              </Flex>
            );
          },
        },
      },
      season: {
        column: {
          name: toTitle(t("season")),
          width: "64px",
        },
        field: {
          name: "season",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { season: string };
            return row.season;
          },
        },
      },
      brand: {
        column: {
          name: toTitle(t("brand")),
          width: "104px",
        },
        field: {
          name: "brand",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              orderConfirmationInvoiceDetailList?: OrderConfirmationInvoiceDetail[];
              proformaInvoiceDetailList?: ProformaInvoiceDetail[];
            };

            const names = (
              row.orderConfirmationInvoiceDetailList ||
              row.proformaInvoiceDetailList ||
              []
            ).map((detail) => detail.brand.name);

            if (names.length === 1) {
              return names[0];
            } else if (names.length > 0) {
              return `${names[0]}(+${names.length - 1})`;
            }
            return "";
          },
        },
      },
      paymentMethod: {
        column: {
          name: toTitle(t("payment_method")),
          width: "104px",
        },
        field: {
          name: "payment_method",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { paymentMethod: string };
            return toTitle(t(`payment_method.${toLower(row.paymentMethod)}`));
          },
        },
      },
      totalAmount: {
        column: {
          name: toTitle(t("total_amount")),
          width: "104px",
          textAlign: "end",
        },
        field: {
          name: "total_amount",
          chakraProps: {
            textAlign: "end",
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & { totalAmount: number };
            return tCurrencyString(row.totalAmount);
          },
        },
      },
      deposit: {
        column: {
          name: toTitle(t("deposit")),
          width: "104px",
          textAlign: "end",
          marginRight: "16px",
        },
        field: {
          name: "deposit",
          chakraProps: {
            textAlign: "end",
            marginRight: "16px",
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              totalRequiredAmount: number;
            };
            return `${tCurrencyString(row.totalRequiredAmount)}`;
          },
        },
      },
      totalRequiredAmount: {
        column: {
          name: toTitle(t("common:invoice_amount")),
          width: "104px",
          textAlign: "end",
          marginRight: "16px",
        },
        field: {
          name: "deposit",
          chakraProps: {
            textAlign: "end",
            marginRight: "16px",
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              totalAmount: Price;
              totalRequiredAmount: Price;
              crossTradeFeeRate?: number | null;
            };
            // 3자무역 프로포마는 인보이스 총액을 수수료를 곱한 금액으로 보여준다(디파짓은 이미 수수료가 곱해진 금액이라 그대로 빠진다)
            const totalAmount =
              _row._rowType === "OrderConfirmationInvoice" || !row.totalAmount
                ? row.totalAmount
                : (withCrossTradeFee(row.totalAmount, row.crossTradeFeeRate) as Price);
            // 인보이스 총액과 송금 필요액이 다르면 총액 / 차이 / 필요액 3줄로 표시.
            // 차이는 항목 이름 없이 숫자로만 — 크레딧·디파짓 등으로 빠졌으면 '-', 늘었으면 '+'.
            if (totalAmount?.value !== row.totalRequiredAmount?.value) {
              const difference = MathUtils.trim(
                (totalAmount?.value ?? 0) - (row.totalRequiredAmount?.value ?? 0)
              );
              const differenceText = tCurrencyString({
                value: Math.abs(difference),
                currency: row.totalAmount.currency,
              });
              return (
                <AppGridCell
                  value={[
                    tCurrencyString(totalAmount),
                    `${difference > 0 ? "-" : "+"}${differenceText}`,
                    tCurrencyString(row.totalRequiredAmount),
                  ]}
                />
              );
            }
            return `${tCurrencyString(row.totalRequiredAmount)}`;
          },
        },
      },
      paymentRequiredAmount: {
        column: {
          name: toTitle(t("common\:payment")),
          width: "104px",
          textAlign: "end",
          marginRight: "16px",
        },
        field: {
          name: "adjustment",
          chakraProps: {
            textAlign: "end",
            marginRight: "16px",
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              totalRequiredAmount: Price;
              payments: {
                type: string;
                sender: { type: string };
                receiver: { type: string };
                amount: Price;
              }[];
              exchangeRate: number;
              bankFee: Price | null;
              adjustmentAmount: Price;
              paidBy: string;
              status: string;
            };
            if (row.status === "REPLACED") {
              return "";
            }
            // OC 는 디파짓 송금이라 바이어 송금 요청 종류가 DEPOSIT 이다(PF 는 물품 대금 PRODUCT).
            const isOrderConfirmation = _row._rowType === "OrderConfirmationInvoice";
            const buyerPaymentType = isOrderConfirmation ? "DEPOSIT" : "PRODUCT";
            const targetPayment = row.payments.find(
              payment => payment.sender.type === "BUYER" && payment.receiver.type === "AGENCY" && payment.type === buyerPaymentType
            );
            return <AppGridCell
              value={[
                <>
                  {/* 부티크로 보낼 유로 — 해외 송금이 끝나도 원화로 바꾸지 않는다(실제 원화는 바이어 송금 요청·차액 줄에 나온다) */}
                  {tCurrencyString(row.totalRequiredAmount)}
                </>,
                row.paidBy === "AGENCY" && <>
                  {targetPayment ? tCurrencyString(targetPayment.amount): ""}
                </>,
                row.adjustmentAmount && <>
                  {tCurrencyString(row.adjustmentAmount)}
                </>
                ]
              }
            />
          },
        },
      },
      buyerRemittedAmount: {
        column: {
          name: toTitle(t("common\:buyer_remitted_amount")),
          width: "112px",
          textAlign: "end",
          marginRight: "16px",
        },
        field: {
          name: "buyerRemittedAmount",
          chakraProps: {
            textAlign: "end",
            marginRight: "16px",
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              totalBuyerToAgencyAmount: number;
              isBuyerToAgencyEur?: boolean;
            };
            if (row.totalBuyerToAgencyAmount) {
              return `${tCurrencyString({
                value: row.totalBuyerToAgencyAmount,
                // 유로 송금 프로포마는 바이어가 유로로 냈다
                currency: row.isBuyerToAgencyEur ? "EUR" : "KRW",
              })}`;
            } else {
              return "";
            }
          },
        },
      },
      agencyRemittedAmount: {
        column: {
          name: toTitle(t("common\:agency_remitted_amount")),
          width: "112px",
          textAlign: "end",
          marginRight: "16px",
        },
        field: {
          name: "agencyRemittedAmount",
          chakraProps: {
            textAlign: "end",
            marginRight: "16px",
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              totalAgencyToBoutiqueAmountKrw: number;
            };
            if (row.totalAgencyToBoutiqueAmountKrw) {
              return `${tCurrencyString({
                value: row.totalAgencyToBoutiqueAmountKrw,
                currency: "KRW",
              })}`;
            } else {
              return "";
            }
          },
        },
      },
      remittanceStatus: {
        column: {
          name: toTitle(t("remittance_status")),
          width: "152px",
        },
        field: {
          name: "remittance_status",
          value: (_row: GridRow, context) => {
            const row = _row as GridRow & {
              isRemitted: boolean;
              isRead?: boolean;
              isRemittedToAgency?: boolean;
              isRemittedToBoutique?: boolean;
              isRemittedAdjustment?: boolean;
              paymentMethod?: AbstractInvoice["paymentMethod"];
              paidBy?: AbstractInvoice["paidBy"];
              status: AbstractInvoice["status"];
              adjustmentAmount: Price | null;
            };

            if (row.status === "REPLACED") {
              return ""
            }

            if (row.paidBy === "BUYER") {
              return (
                <AppRemittanceStatusLabel
                  to={"BOUTIQUE"}
                  value={
                    row.paymentMethod === "SKIP"
                      ? "SKIPPED"
                      : row.isRemittedToBoutique
                      ? "REMITTED"
                      : "UNREMITTED"
                  }
                />
              );
            } else if (row.paidBy === "AGENCY") {
              return (
                <AppGridCell
                  value={[
                    <AppRemittanceStatusLabel
                      key={"to_boutique"}
                      to={"BOUTIQUE"}
                      value={
                        row.paymentMethod === "SKIP"
                          ? "SKIPPED"
                          : row.isRemittedToBoutique
                          ? "REMITTED"
                          : "UNREMITTED"
                      }
                    />,
                    <AppRemittanceStatusLabel
                      key={"to_agency"}
                      to={"AGENCY"}
                      value={
                        row.paymentMethod === "SKIP"
                          ? "SKIPPED"
                          : row.isRemittedToAgency
                            ? "REMITTED"
                            : "UNREMITTED"
                      }
                    />,
                    row.isRemittedToBoutique && row.isRemittedToAgency && row.adjustmentAmount && <AppRemittanceStatusLabel
                      key={"adjustment"}
                      to={"ADJUSTMENT"}
                      value={
                        row.paymentMethod === "SKIP"
                          ? "SKIPPED"
                          : row.isRemittedAdjustment
                            ? "REMITTED"
                            : "UNREMITTED"
                      }
                    />
                  ]}
                />
              );
            } else {
              return (
                <AppRemittanceStatusLabel
                  value={
                    row.paymentMethod === "SKIP"
                      ? "SKIPPED"
                      : row.isRemitted
                      ? "REMITTED"
                      : "UNREMITTED"
                  }
                />
              );
            }
          },
        },
      },
      paidBy: {
        column: {
          name: toTitle(t("paid_by")),
          width: "104px",
        },
        field: {
          name: "paid_by",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { paidBy: string };
            return toTitle(t(toLower(row.paidBy)));
          },
        },
      },
      issuedBy: {
        column: {
          name: toTitle(t("issued_by")),
          width: "104px",
        },
        field: {
          name: "issued_by",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              issuedByCompany: { name: string };
            };
            return row.issuedByCompany.name;
          },
        },
      },
      issuedThrough: {
        column: {
          name: toTitle(t("issued_through")),
          width: "104px",
        },
        field: {
          name: "issued_through",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              issuedThroughCompany: { name: string };
            };
            return row.issuedThroughCompany.name;
          },
        },
      },
      orderedBy: {
        column: {
          name: toTitle(t("ordered_by")),
          width: "104px",
        },
        field: {
          name: "ordered_by",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              issuedToCompany: { name: string };
            };
            return row.issuedToCompany.name;
          },
        },
      },
      tatr: {
        column: {
          name: "tatr",
          width: "132px",
          value: (
            <AppGridCell
              alignItems={"flex-end"}
              value={[
                toTitle(t("total_amount")),
                toTitle(t("common:total_remittance_required_amount.abbr")),
              ]}
            />
          ),
        },
        field: {
          name: "tatr",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              totalAmount: number;
              totalRequiredAmount: number;
              totalBuyerToBoutiqueAmount: number;
              totalBuyerToAgencyAmount: number;
            };
            return (
              <AppGridCell
                alignItems={"flex-end"}
                value={[
                  tCurrencyString(row.totalAmount),
                  tCurrencyString(
                    row.totalRequiredAmount -
                      row.totalBuyerToBoutiqueAmount -
                      row.totalBuyerToAgencyAmount
                  ),
                ]}
              />
            );
          },
        },
      },
      issuedOn: {
        column: {
          name: toTitle(t("issued_on")),
          width: "96px",
        },
        field: {
          name: "issued_on",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { issuedOn: Date };
            return tLocalDateString(row.issuedOn);
          },
        },
      },
      amount: {
        column: {
          name: toTitle(t("amount")),
          width: "114px",
          textAlign: "end",
          marginRight: "16px"
        },
        field: {
          name: "amount",
          chakraProps: {
            textAlign: "end",
            marginRight: "16px"
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & { amount: number };
            return tCurrencyString(row.amount);
          },
        },
      },
      creditInvoiceType: {
        column: {
          name: toTitle(t("type")),
          width: "64px",
        },
        field: {
          name: "type",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { type: string };
            return toUpper(t(`credit_invoice.type.${toLower(row.type)}`));
          },
        },
      },
      // 크레딧 목록: 소유자 / 발행 사유 / 사용 여부
      ownedBy: {
        column: {
          name: toTitle(t("owned_by_company_type")),
          width: "72px",
        },
        field: {
          name: "owned_by",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              ownedByCompany?: { type: string } | null;
            };
            return row.ownedByCompany?.type === "AGENCY"
              ? toTitle(t("agency"))
              : toTitle(t("buyer"));
          },
        },
      },
      creditReason: {
        column: {
          name: toTitle(t("credit_issue_reason")),
          width: "minmax(160px, 2fr)",
        },
        field: {
          name: "credit_reason",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { publicComment?: string | null };
            return (
              <Box
                overflow={"hidden"}
                textOverflow={"ellipsis"}
                whiteSpace={"nowrap"}
                title={row.publicComment ?? undefined}
              >
                {row.publicComment || "-"}
              </Box>
            );
          },
        },
      },
      creditUsage: {
        column: {
          name: toTitle(t("usage_status")),
          width: "64px",
        },
        field: {
          name: "credit_usage",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { isUsed?: boolean };
            return toTitle(t(row.isUsed ? "used" : "unused"));
          },
        },
      },
      number: {
        column: {
          name: toTitle(t("number")),
          width: "minmax(120px, 1fr)",
        },
        field: {
          name: "number",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { number: string };
            return row.number;
          },
        },
      },
      sender: {
        column: {
          name: toTitle(t("sender")),
          width: "104px",
        },
        field: {
          name: "sender",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              sender: { name: string };
            };
            return row.sender.name;
          },
        },
      },
      receiver: {
        column: {
          name: toTitle(t("receiver")),
          width: "104px",
        },
        field: {
          name: "receiver",
          value: (_row: GridRow) => {
            const row = _row as GridRow & {
              receiver: { name: string };
            };
            return row.receiver.name;
          },
        },
      },
      remittedAmount: {
        column: {
          name: toTitle(t("remitted_amount")),
          width: "104px",
          textAlign: "end",
        },
        field: {
          name: "remitted_amount",
          chakraProps: {
            textAlign: "end",
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & { remittedAmount: Price };
            return tCurrencyString(row.remittedAmount);
          },
        },
      },
      unremittedAmount: {
        column: {
          name: toTitle(t("unremitted_amount")),
          width: "104px",
          textAlign: "end",
          marginRight: "16px"
        },
        field: {
          name: "unremitted_amount",
          chakraProps: {
            textAlign: "end",
            marginRight: "16px"
          },
          value: (_row: GridRow) => {
            const row = _row as GridRow & { unremittedAmount: Price };
            return tCurrencyString(row.unremittedAmount);
          },
        },
      },
      requestedOn: {
        column: {
          name: toTitle(t("requested_on")),
          width: "104px",
        },
        field: {
          name: "requested_on",
          value: (_row: GridRow) => {
            const row = _row as GridRow & { createdAt: Date };
            return tLocalDateString(row.createdAt);
          },
        },
      },
    };
  }, [t, tCurrencyString, tLocalDateString]);

  //actions

  const downloadInvoice = useMemo(() => {
    interface Foo extends GridRow {
      file: Pick<StoredObject, "id" | "name"> | null;
      compressedFile: Pick<StoredObject, "id" | "name"> | null;
    }

    return appGrid.actions.withDownloadUnoFile<Foo>(
      (row) => row.file || row.compressedFile
    );
  }, [appGrid.actions]);

  const showBankRemittanceReceiptUploadModal = useMemo(() => {
    interface Foo extends GridRow {
      file: Pick<StoredObject, "id" | "name"> | null;
      compressedFile: Pick<StoredObject, "id" | "name"> | null;
    }

    return appGrid.actions.withDownloadUnoFile<Foo>(
      (row) => row.file || row.compressedFile
    );
  }, [appGrid.actions]);

  const actions = useMemo(() => {
    return {
      downloadInvoice,
      showBankRemittanceReceiptUploadModal
    };
  }, [
    downloadInvoice,
    showBankRemittanceReceiptUploadModal
  ]);

  //extraContents

  // const invoiceFigureExtraContents = useCallback(
  //   (_row: GridRow, meta: AppGridMeta) => {
  //     const row = _row as InvoiceFiguresRow;
  //
  //     const rightSpacerWidth = meta.actions ? "96px" : undefined;
  //
  //     const extraComponents = [];
  //
  //     const alternativeCurrencyUsage = row.figuresByBrandList
  //       .map((figuresByBrand) => {
  //         return {
  //           usedCredit: figuresByBrand.usedCredit,
  //           usedDeposit: figuresByBrand.usedDeposit,
  //         };
  //       })
  //       .reduce(
  //         (acc, curr) => {
  //           return {
  //             usedCredit: acc.usedCredit + curr.usedCredit,
  //             usedDeposit: acc.usedDeposit + curr.usedDeposit,
  //           };
  //         },
  //         {
  //           usedCredit: 0,
  //           usedDeposit: 0,
  //         }
  //       );
  //
  //     if (
  //       alternativeCurrencyUsage.usedCredit > 0 ||
  //       alternativeCurrencyUsage.usedDeposit > 0
  //     ) {
  //       const orderConfirmationRow = findParentGridRow(row, rows) as GridRow & {
  //         season: string;
  //       };
  //
  //       extraComponents.push(
  //         <InvoiceAlternativeCurrencyUsageTable
  //           key={"InvoiceAlternativeCurrencyUsageTable_" + row.id}
  //           leftSpacerWidth={"1fr"}
  //           rightSpacerWidth={rightSpacerWidth}
  //           usage={{
  //             credit: alternativeCurrencyUsage.usedCredit * -1,
  //             deposit: alternativeCurrencyUsage.usedDeposit * -1,
  //           }}
  //           season={orderConfirmationRow.season}
  //         />
  //       );
  //     }
  //
  //     extraComponents.push(
  //       <InvoiceFiguresByBrandListTable
  //         key={"InvoiceFiguresByBrandListTable_" + row.id}
  //         leftSpacerWidth={"1fr"}
  //         rightSpacerWidth={rightSpacerWidth}
  //         figuresByBrand={row.figuresByBrandList}
  //       />
  //     );
  //
  //     return extraComponents;
  //   },
  //   [rows]
  // );

  const invoiceCommentExtraContents = useCallback((_row: GridRow) => {
    const row = _row as InvoiceCommentRow;
    return [<Box key={"InvoicePublicComment_" + row.id}>{row.content}</Box>];
  }, []);

  const extraContents = useMemo(() => {
    return {
      invoiceCommentExtraContents,
    };
  }, [invoiceCommentExtraContents]);

  const withPublicComment = useCallback(
    (isBuyer: boolean = true): AppGridMeta => {
      return {
        rowType: "PublicComment",
        columns: [{ name: "RowType", width: "1fr" }],
        fields: [
          {
            name: "RowType",
            value: (_row: GridRow) => {
              if (isBuyer) {
                return `${toTitle(t("comment"))}`;
              } else {
                return `${toTitle(t("public_comment"))} (${t(
                  "public_comment.note"
                )})`;
              }
            },
          },
        ],
        extraContents: invoiceCommentExtraContents,
        actions: [],
      };
    },
    [invoiceCommentExtraContents, t]
  );

  const privateComment = useMemo((): AppGridMeta => {
    return {
      rowType: "PrivateComment",
      columns: [{ name: "RowType", width: "1fr" }],
      fields: [
        {
          name: "RowType",
          value: (_row: GridRow) => {
            return `${toTitle(t("private_comment"))}`;
          },
        },
      ],
      extraContents: invoiceCommentExtraContents,
      actions: [],
    };
  }, [invoiceCommentExtraContents, t]);

  function insertPartials(
    meta: AppGridMeta,
    partials: { field: AppGridField; column: AppGridColumn }[],
    location?: {
      after: string;
    }
  ): AppGridMeta {
    const i = location
      ? meta.columns.findIndex((c) => c.name === location.after)
      : 0;
    if (i > -1) {
      const columns = [
        ...meta.columns.slice(0, i + 1),
        ...partials.map((t) => t.column),
        ...meta.columns.slice(i + 1),
      ];
      const fields = [
        ...meta.fields.slice(0, i + 1),
        ...partials.map((t) => t.field),
        ...meta.fields.slice(i + 1),
      ];
      return {
        ...meta,
        columns,
        fields,
      };
    }

    return {
      ...meta,
    };
  }

  const orderSheetSet = useMemo((): AppGridMeta => {
    return {
      rowType: "OrderSheetSet",
      columns: [
        { name: "RowType", width: "1fr" },
        { name: "Name", width: "1fr" },
        { name: "Status", width: "1fr" },
      ],
      fields: [
        appGrid.fields.withRowType("order_sheet_set"),
        {
          name: "Name",
          value: (_row) => {
            const row = _row as BuyerLinkedOrderSheetSetRow;
            return row.name;
          },
        },
        orderSheetGrid.fields.withOrderSheetStatus(),
      ],
      indicator: (_row) => {
        return <GridRowIndicator type={"EMPTY"} isHovered={_row.isHovered} />;
      },
      actions: [
        appGrid.actions.withDownloadUnoFile<BuyerLinkedOrderSheetSetRow>(
          (row) => row.compressedExportedFile
        ),
      ],
    };
  }, [appGrid.actions, appGrid.fields, orderSheetGrid.fields]);

  const orderSheet = useMemo((): AppGridMeta => {
    return {
      rowType: "OrderSheet",
      columns: [
        { name: "RowType", width: "1fr" },
        { name: "Name", width: "1fr" },
        { name: "Status", width: "1fr" },
      ],
      fields: [
        appGrid.fields.withRowType("order_sheet"),
        {
          name: "Name",
          value: (_row) => {
            const row = _row as AgencyLinkedOrderSheetRow;
            return row.name;
          },
        },
        orderSheetGrid.fields.withOrderSheetStatus(),
      ],
      actions: [
        orderSheetGrid.actions.editOrViewOrderSheet,
        appGrid.actions.withDownloadUnoFile<AgencyLinkedOrderSheetRow>(
          (row) => row.exportedFile || row.uploadedFile
        ),
      ],
      extraContents: orderSheetGrid.extraContents.orderSheetExtraContents,
    };
  }, [
    appGrid.actions,
    appGrid.fields,
    orderSheetGrid.actions.editOrViewOrderSheet,
    orderSheetGrid.extraContents.orderSheetExtraContents,
    orderSheetGrid.fields,
  ]);

  const metas = useMemo(() => {
    return {
      withPublicComment,
      privateComment,
      orderSheetSet,
      orderSheet,
    };
  }, [withPublicComment, privateComment, orderSheetSet, orderSheet]);

  return {
    actions,
    extraContents,
    fields,
    metas,
    pairs,
  };
}
